import { z } from 'zod';
import { isValidTimeZone } from '../../dates/index.js';
import { newTagsSchema } from '../cal-tag/new-tags-schema.js';
import { nullableUuidString, optionalPresentUuidString, requiredUuidString, } from '../zod-helpers/index.js';
import { calEventAssigneeSchema } from './cal-event-assignee-schema.js';
import { calEventTagIdsSchema } from './cal-event-tag-ids-schema.js';
import { venueAddressSchema } from './venue-address-schema.js';
const EDITABLE_KEYS = [
    'name',
    'note',
    'dateStart',
    'dateEnd',
    'allDay',
    'timeZone',
    'promotionId',
    'ownerUserId',
    'assignees',
    'venueAddress',
    'needsApproval',
    'checkInRequired',
    'underReviewRequired',
    'photoRequired',
    'requiresProductSummary',
    'tagIds',
];
export const updateCalEventInputSchema = z
    .object({
    eventId: requiredUuidString('Event is required.', 'Invalid event ID.'),
    name: z
        .string()
        .trim()
        .min(1, 'Name is required.')
        .max(128, 'Name must be 128 characters or fewer.')
        .optional(),
    note: z.string().max(4096, 'Note must be 4096 characters or fewer.').nullish(),
    dateStart: z.string().datetime({ message: 'Invalid start date.' }).optional(),
    dateEnd: z.string().datetime({ message: 'Invalid end date.' }).optional(),
    allDay: z.boolean().optional(),
    timeZone: z.string().refine(isValidTimeZone, 'Invalid time zone.').optional(),
    promotionId: nullableUuidString('Invalid promotion.').optional(),
    ownerUserId: optionalPresentUuidString('Invalid owner.').optional(),
    assignees: z
        .array(calEventAssigneeSchema)
        .max(50, 'An event can have at most 50 people.')
        .optional(),
    venueAddress: venueAddressSchema.nullish(),
    needsApproval: z.boolean().optional(),
    checkInRequired: z.boolean().optional(),
    underReviewRequired: z.boolean().optional(),
    photoRequired: z.boolean().optional(),
    requiresProductSummary: z.boolean().optional(),
    tagIds: calEventTagIdsSchema.optional(),
    newTags: newTagsSchema,
})
    .superRefine((data, ctx) => {
    if (EDITABLE_KEYS.every((key) => data[key] === undefined) && data.newTags.length === 0) {
        ctx.addIssue({ code: 'custom', message: 'Change at least one field.', path: [] });
    }
    if (data.dateStart && data.dateEnd && new Date(data.dateEnd) <= new Date(data.dateStart)) {
        ctx.addIssue({
            code: 'custom',
            message: 'End date/time must be after start date/time.',
            path: ['dateEnd'],
        });
    }
    const userIds = (data.assignees ?? []).map((assignee) => assignee.userId);
    if (new Set(userIds).size !== userIds.length) {
        ctx.addIssue({
            code: 'custom',
            message: 'Each person can only be added once.',
            path: ['assignees'],
        });
    }
});
//# sourceMappingURL=update-cal-event-input.js.map